import { MesfModal } from "@dexteel/mesf-core";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import {
	Box,
	Button,
	IconButton,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { useMemo, useState } from "react";

import { HeatMechanicalCurve } from "../../models/HeatMechanicalCurve";
import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { getHeatMechanicalCurves } from "../../repositories/CampaignRepository";
import { formatTimestamp } from "./format";
import { seqColor } from "./layout";

type Mode = "stress" | "force";

export const useHeatMechanicalCurves = (heatId?: number | null) =>
	useQuery<HeatMechanicalCurve[], Error>({
		queryKey: ["heatMechanicalCurves", heatId],
		queryFn: ({ signal }) =>
			getHeatMechanicalCurves({ heatId: heatId! }, signal),
		enabled: !!heatId,
		staleTime: 5 * 60 * 1000,
	});

type Props = {
	heatId: number;
	heatName?: string | null;
	results: HeatMechanicalResult[];
	fill?: boolean;
	runId?: number;
};

export const MechanicalCurvesChart = ({
	heatId,
	heatName,
	results,
	fill = false,
	runId,
}: Props) => {
	const theme = useTheme();
	const { data: curves } = useHeatMechanicalCurves(heatId);
	const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
	const [mode, setMode] = useState<Mode>("stress");
	const [expanded, setExpanded] = useState(false);

	const runs = useMemo(() => {
		const byRun = new Map<number, HeatMechanicalCurve[]>();
		for (const curve of curves ?? []) {
			const run = byRun.get(curve.MechanicalTestRunId) ?? [];
			run.push(curve);
			byRun.set(curve.MechanicalTestRunId, run);
		}
		return [...byRun.entries()].map(([runId, specimens]) => ({
			runId,
			reception: specimens[0].Reception,
			specimens,
		}));
	}, [curves]);

	const run =
		runId != null
			? runs.find((r) => r.runId === runId)
			: (runs.find((r) => r.runId === selectedRunId) ?? runs[0]);
	const canShowStress =
		!!run && run.specimens.every((s) => s.Area && s.GaugeLength);
	const effectiveMode: Mode = canShowStress ? mode : "force";

	const option = useMemo<EChartsOption | null>(() => {
		if (!run) return null;
		const ink = theme.palette.text.secondary;
		const grid = theme.palette.divider;
		const stress = effectiveMode === "stress";

		// Requirement lines of the run's grade (ksi), stress view only
		const limits = results.find((r) => r.MechanicalTestRunId === run.runId);
		// Limits on the same value share one line and label (e.g. Yield max = Tensile min)
		const limitLines: { name: string; value: number }[] = [];
		if (stress) {
			for (const [name, value] of [
				["Yield min", limits?.YieldMinKsi],
				["Yield max", limits?.YieldMaxKsi],
				["Tensile min", limits?.TensileMinKsi],
				["Tensile max", limits?.TensileMaxKsi],
			] as const) {
				if (value == null) continue;
				const same = limitLines.find((l) => l.value === value);
				if (same) same.name += ` · ${name}`;
				else limitLines.push({ name, value });
			}
		}

		const series = run.specimens.map((s, i) => {
			const { disp, force } = s.Curve;
			const data = disp.map((d, k) =>
				stress
					? [(d / s.GaugeLength!) * 100, force[k] / s.Area! / 1000]
					: [d, force[k]],
			);
			return {
				name: `Seq ${s.SampleSequence}`,
				type: "line" as const,
				data,
				showSymbol: false,
				lineStyle: { width: 2 },
				color: seqColor(theme, s.SampleSequence),
				emphasis: { focus: "series" as const },
				markLine:
					i === 0 && limitLines.length
						? {
								silent: true,
								symbol: "none",
								lineStyle: { type: "dashed" as const, color: ink, width: 1 },
								label: {
									formatter: (p: any) => `${p.name} ${p.value} ksi`,
									position: "insideEndTop" as const,
									color: ink,
									fontSize: 10,
								},
								data: limitLines.map((l) => ({
									name: l.name,
									yAxis: l.value,
								})),
							}
						: undefined,
			};
		});

		const xUnit = stress ? "%" : "in";
		const yUnit = stress ? "ksi" : "lbf";
		const axis = {
			nameTextStyle: { color: ink, fontSize: 11 },
			axisLabel: { color: ink, fontSize: 10 },
			axisLine: { lineStyle: { color: grid } },
			splitLine: { lineStyle: { color: grid, opacity: 0.6 } },
		};

		return {
			animation: false,
			grid: { top: 40, left: 16, right: 16, bottom: 24, containLabel: true },
			legend: {
				top: 0,
				right: 0,
				icon: "roundRect",
				itemWidth: 14,
				itemHeight: 3,
				textStyle: { color: theme.palette.text.primary, fontSize: 11 },
			},
			tooltip: {
				trigger: "axis",
				axisPointer: { type: "cross", label: { precision: 2 } },
				textStyle: { fontSize: 11 },
				formatter: (params: any) => {
					const items = Array.isArray(params) ? params : [params];
					if (!items.length) return "";
					const x = Number(items[0].value[0]);
					const head = stress
						? `Strain ${x.toFixed(2)} %`
						: `Displacement ${x.toFixed(3)} in`;
					return [
						head,
						...items.map(
							(p: any) =>
								`${p.marker} ${p.seriesName}: <b>${Number(p.value[1]).toLocaleString("en-US", { maximumFractionDigits: stress ? 1 : 0 })} ${yUnit}</b>`,
						),
					].join("<br/>");
				},
			},
			xAxis: {
				type: "value",
				name: stress ? "Strain (%, crosshead)" : "Displacement (in)",
				nameLocation: "middle",
				nameGap: 26,
				min: 0,
				...axis,
				axisLabel: { ...axis.axisLabel, formatter: `{value} ${xUnit}` },
			},
			yAxis: {
				type: "value",
				name: stress ? "Stress (ksi)" : "Force (lbf)",
				nameLocation: "end",
				nameGap: 12,
				min: 0,
				...axis,
				nameTextStyle: { ...axis.nameTextStyle, align: "left" },
			},
			series,
		};
	}, [run, effectiveMode, results, theme]);

	const modalOption = useMemo<EChartsOption | null>(() => {
		if (!option) return null;
		const ink = theme.palette.text.secondary;
		return {
			...option,
			grid: { top: 40, left: 16, right: 16, bottom: 64, containLabel: true },
			legend: { ...(option.legend as object), right: 112 },
			yAxis: { ...(option.yAxis as object), min: undefined, scale: true },
			toolbox: {
				right: 0,
				top: -4,
				itemSize: 14,
				iconStyle: { borderColor: ink },
				feature: {
					dataZoom: {
						filterMode: "none",
						title: { zoom: "Zoom area", back: "Undo zoom" },
					},
					restore: { title: "Reset zoom" },
					saveAsImage: {
						title: "Download as PNG",
						name: `tensile-curves-heat-${heatName ?? heatId}-${formatTimestamp(run?.reception).replace(/\D+/g, "")}`,
						backgroundColor: theme.palette.background.paper,
						pixelRatio: 2,
						excludeComponents: ["toolbox", "dataZoom"],
					},
				},
			},
			dataZoom: [
				{ type: "inside", xAxisIndex: 0, filterMode: "filter" },
				{
					type: "slider",
					xAxisIndex: 0,
					filterMode: "filter",
					bottom: 8,
					height: 18,
					labelFormatter: "",
				},
			],
		};
	}, [option, theme, heatName, heatId, run]);

	if (!run || !option || !modalOption) return null;

	const controls = (
		<>
			{runId == null && runs.length > 1 && (
				<ToggleButtonGroup
					size="small"
					exclusive
					value={run.runId}
					onChange={(_, v) => v != null && setSelectedRunId(v)}
					aria-label="Test run"
				>
					{runs.map((r) => (
						<ToggleButton
							key={r.runId}
							value={r.runId}
							sx={{ py: 0.25, fontSize: 11 }}
						>
							{formatTimestamp(r.reception)}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
			)}
			{canShowStress && (
				<ToggleButtonGroup
					size="small"
					exclusive
					value={effectiveMode}
					onChange={(_, v) => v && setMode(v)}
					aria-label="Chart axes"
				>
					<ToggleButton value="stress" sx={{ py: 0.25, fontSize: 11 }}>
						Stress-strain
					</ToggleButton>
					<ToggleButton value="force" sx={{ py: 0.25, fontSize: 11 }}>
						Force-displacement
					</ToggleButton>
				</ToggleButtonGroup>
			)}
		</>
	);
	const toolbarSx = {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 1,
	} as const;

	return (
		<Box
			sx={
				fill
					? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
					: { mt: 1.5 }
			}
		>
			<Box sx={{ ...toolbarSx, mb: 0.5 }}>
				<Typography
					variant="subtitle2"
					fontWeight={600}
					color="text.secondary"
					sx={{ flex: 1 }}
				>
					Tensile Test Curves
				</Typography>
				{controls}
				<IconButton
					size="small"
					title="Open full size"
					aria-label="Open full size"
					onClick={() => setExpanded(true)}
				>
					<OpenInFullIcon fontSize="small" />
				</IconButton>
			</Box>
			<Box
				sx={
					fill
						? { flex: 1, minHeight: 230, position: "relative" }
						: { height: 280 }
				}
			>
				<ReactECharts
					option={option}
					notMerge
					style={
						fill
							? {
									position: "absolute",
									inset: 0,
									height: "100%",
									width: "100%",
								}
							: { height: "100%", width: "100%" }
					}
				/>
			</Box>
			<MesfModal
				open={expanded}
				handleClose={() => setExpanded(false)}
				id="mechanical-curves-modal"
				maxWidth="xl"
				title={`TENSILE TEST CURVES - HEAT ${heatName ?? "-"}`}
				subtitle={`Received ${formatTimestamp(run.reception)}`}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }}>
					<Box sx={{ width: "min(1400px, 80vw)" }}>
						<Box sx={{ ...toolbarSx, justifyContent: "flex-end", mb: 1 }}>
							{controls}
						</Box>
						<ReactECharts
							option={modalOption}
							notMerge
							style={{ height: "65vh", width: "100%" }}
						/>
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "10px 30px 20px" }}>
					<Button
						onClick={() => setExpanded(false)}
						variant="contained"
						color="inherit"
					>
						Close
					</Button>
				</MesfModal.Actions>
			</MesfModal>
		</Box>
	);
};
